import moment from "moment";

import {
  computeRelativeDate,
  tryParseRelativeDateOffsetValue,
} from "../../datetime.js";
import { DATE_FORMAT_NO_TIME, RelativeDateUnit } from "../../datetimeType.js";
import { FilledDynamicValueTableColumnType } from "../../DynamicValue.js";
import { assertNever } from "../../errors.js";
import { isIsoDate } from "../../index.js";

export type DateFilterMode = "legacy" | "optimized";

/**
 * Encapsulates transformation logic applied to date/datetime filters.
 *
 * Downstream code should apply all transformation functions from a single DateFilterMode.
 * The resulting hql queries are intended to represent the same semantics.
 */
export interface DateFilterTransformations {
  /**
   * Potentially cast a column (e.g., ToDate / truncation) based on args/column type
   */
  maybeDateCastColumn: (input: {
    args: string[];
    column: string;
    columnType: FilledDynamicValueTableColumnType;
    operation?: string;
  }) => {
    column: string;
    columnType: FilledDynamicValueTableColumnType;
    truncationApplied: boolean;
  };

  /**
   * Wrap a param ref with ToDate/ToDatetime and optional truncation
   */
  castDateOrDatetimeArg: (input: {
    arg: string; // already jinja-wrapped when needed
    columnType: FilledDynamicValueTableColumnType;
    relativeDateTruncUnit?: RelativeDateUnit | null;
  }) => string;

  /**
   * Process the operation and arg for optimizations (e.g., DATE_EQUAL_OR_BEFORE -> DATE_BEFORE)
   */
  processBinaryColumnPredicate: (input: { operation: string; arg: string }) => {
    operation: string;
    arg: string;
  };

  /**
   * Normalize DATE_BETWEEN bounds and inclusivity
   */
  normalizeBetweenBounds: (input: {
    args: string[];
    columnType: FilledDynamicValueTableColumnType;
    truncationApplied: boolean;
  }) => NormalizedBetween;

  /**
   * For optimized handling of DATE_EQUAL on datetime-like columns with ISO date args
   */
  maybeRewriteDateEqual: (input: {
    columnType: FilledDynamicValueTableColumnType;
    arg: string;
    hasJinja?: boolean;
  }) => { lowerArg: string; upperArg: string } | undefined;
}

type NormalizedBetween = {
  lowerArg: string;
  upperArg: string;
  lowerInclusive: boolean;
  upperInclusive: boolean;
};

function isDateTimeColumnType(
  columnType: FilledDynamicValueTableColumnType,
): boolean {
  return columnType === "DATETIME" || columnType === "DATETIMETZ";
}

function applyRelativeDateTruncation(
  column: string,
  unit: RelativeDateUnit,
): { column: string; columnType: FilledDynamicValueTableColumnType } {
  switch (unit) {
    case RelativeDateUnit.YEAR:
      return {
        column: `TruncYear(ToDatetime(${column}))`,
        columnType: "DATE",
      };
    case RelativeDateUnit.MONTH:
      return {
        column: `TruncMonth(ToDatetime(${column}))`,
        columnType: "DATE",
      };
    case RelativeDateUnit.QUARTER:
      return {
        column: `TruncQuarter(ToDatetime(${column}))`,
        columnType: "DATE",
      };
    case RelativeDateUnit.WEEK:
      return {
        column: `TruncWeek(ToDatetime(${column}))`,
        columnType: "DATE",
      };
    case RelativeDateUnit.DAY:
      return {
        column: `ToDate(${column})`,
        columnType: "DATE",
      };
    default:
      assertNever(unit, unit);
  }
}

function applyRelativeDateArgTruncation(
  arg: string,
  unit: RelativeDateUnit,
): string {
  switch (unit) {
    case RelativeDateUnit.YEAR:
      return `TruncYear(ToDatetime(${arg}))`;
    case RelativeDateUnit.QUARTER:
      return `TruncQuarter(ToDatetime(${arg}))`;
    case RelativeDateUnit.MONTH:
      return `TruncMonth(ToDatetime(${arg}))`;
    case RelativeDateUnit.WEEK:
      return `TruncWeek(ToDatetime(${arg}))`;
    case RelativeDateUnit.DAY:
    default:
      return arg; // fallthrough for columnType specific handling
  }
}

export function getZeroOffsetBoundsForUnit(
  unit: RelativeDateUnit,
  timezone: string,
): { lower: string; upper: string } {
  const base = moment().tz(timezone);
  switch (unit) {
    case RelativeDateUnit.YEAR: {
      const start = base.clone().startOf("year");
      return {
        lower: start.format(DATE_FORMAT_NO_TIME),
        upper: start.clone().add(1, "year").format(DATE_FORMAT_NO_TIME),
      };
    }
    case RelativeDateUnit.QUARTER: {
      const start = base.clone().startOf("quarter");
      return {
        lower: start.format(DATE_FORMAT_NO_TIME),
        upper: start.clone().add(1, "quarter").format(DATE_FORMAT_NO_TIME),
      };
    }
    case RelativeDateUnit.MONTH: {
      const start = base.clone().startOf("month");
      return {
        lower: start.format(DATE_FORMAT_NO_TIME),
        upper: start.clone().add(1, "month").format(DATE_FORMAT_NO_TIME),
      };
    }
    case RelativeDateUnit.DAY: {
      const start = base.clone().startOf("day");
      return {
        lower: start.format(DATE_FORMAT_NO_TIME),
        upper: start.clone().add(1, "day").format(DATE_FORMAT_NO_TIME),
      };
    }
    case RelativeDateUnit.WEEK: {
      // This should never get called, weeks are always resolved in hex-sl.
      throw new Error("Unexpected error processing relative date filter.");
    }
    default:
      assertNever(unit, unit);
  }
}

function rewriteIsoOpForResolvedDate(
  operation: string,
  arg: string,
): { operation: string; arg: string } {
  // Optimization: Convert DATE_EQUAL_OR_BEFORE to DATE_BEFORE with date + 1 day
  if (operation === "DATE_EQUAL_OR_BEFORE" && isIsoDate(arg)) {
    const m = moment(arg);
    if (m.isValid()) {
      const nextDay = m.clone().add(1, "day");
      return {
        operation: "DATE_BEFORE",
        arg: nextDay.format(DATE_FORMAT_NO_TIME),
      };
    }
  }

  // Optimization: Convert DATE_AFTER to DATE_EQUAL_OR_AFTER with date + 1 day
  if (operation === "DATE_AFTER" && isIsoDate(arg)) {
    const m = moment(arg);
    if (m.isValid()) {
      const nextDay = m.clone().add(1, "day");
      return {
        operation: "DATE_EQUAL_OR_AFTER",
        arg: nextDay.format(DATE_FORMAT_NO_TIME),
      };
    }
  }

  return { operation, arg };
}

function rewriteForZeroOffsetRelative(
  operation: string,
  unit: RelativeDateUnit,
  timezone: string,
): { operation: string; arg: string } | undefined {
  // WEEK remains unchanged due to DB-specific week semantics
  if (unit === RelativeDateUnit.WEEK) return undefined;

  const { lower, upper } = getZeroOffsetBoundsForUnit(unit, timezone);
  switch (operation) {
    case "DATE_EQUAL_OR_BEFORE":
      return { operation: "DATE_BEFORE", arg: upper }; // < next-start
    case "DATE_BEFORE":
      return { operation: "DATE_BEFORE", arg: lower }; // < start
    case "DATE_AFTER":
      return { operation: "DATE_EQUAL_OR_AFTER", arg: upper }; // >= next-start
    case "DATE_EQUAL_OR_AFTER":
      return { operation: "DATE_EQUAL_OR_AFTER", arg: lower }; // >= start
    default:
      // DATE_EQUAL handled elsewhere; DATE_NOT_EQUAL unchanged
      return undefined;
  }
}

function checkArgumentTypes(args: string[]): {
  isIsoDateArgs: boolean;
  isRelativeDateArgs: boolean;
} {
  const isIsoDateArgs = args.some(isIsoDate);
  const isRelativeDateArgs = args.some(
    (arg) => tryParseRelativeDateOffsetValue(arg) != null,
  );
  return { isIsoDateArgs, isRelativeDateArgs };
}

function castDateOrDatetimeArgImpl(input: {
  arg: string;
  columnType: FilledDynamicValueTableColumnType;
  relativeDateTruncUnit?: RelativeDateUnit | null;
}): string {
  const { arg, columnType, relativeDateTruncUnit } = input;

  if (
    columnType === "DATE" ||
    columnType === "DATETIME" ||
    columnType === "DATETIMETZ"
  ) {
    if (relativeDateTruncUnit != null) {
      const truncatedArg = applyRelativeDateArgTruncation(
        arg,
        relativeDateTruncUnit,
      );
      if (truncatedArg !== arg) {
        return truncatedArg;
      }
    }

    if (columnType === "DATE") {
      return `ToDate(${arg})`;
    } else {
      return `ToDatetime(${arg})`;
    }
  }

  return arg;
}

/**
 * These transformations were used prior to September 2025.
 */
const legacyDateFilterTransformations: DateFilterTransformations = {
  /**
   * This casts a datetime column to a date if filtering by a date string so that
   * we have consistent behavior when filtering date + datetime columns.
   *
   * For example, if we have a datetime column and we have a filter `<=
   * 2024-10-01`, without casting the column to a date, any values larger than
   * `2024-10-01 00:00:00` would be excluded. In this case, our desired behavior
   * is to return all rows that are before or on `2024-10-01` (ex. 2024-10-01
   * 00:00:01), so casting the column to a date gives us this behavior.
   */
  maybeDateCastColumn: ({
    args,
    column,
    columnType,
  }: {
    args: string[];
    column: string;
    columnType: FilledDynamicValueTableColumnType;
    operation?: string;
  }) => {
    // For very specific relative date use cases:
    // - non-range
    // - 0 value offset, used to filter by the current year/month/week/day
    // we use the offset unit to determine what the truncation level should be.
    // For example, if the relative date is "this year", the offset is 0 and the
    // offset unit is year. In this case, we truncate to the year level.
    const relativeDateArg =
      args.length === 1 ? tryParseRelativeDateOffsetValue(args[0]!) : null;
    if (relativeDateArg != null && relativeDateArg.amount === 0) {
      const truncated = applyRelativeDateTruncation(
        column,
        relativeDateArg.unit,
      );
      return { ...truncated, truncationApplied: true };
    }

    const { isIsoDateArgs, isRelativeDateArgs } = checkArgumentTypes(args);

    if (
      // If for whatever reason the column type is unknown we should still try to
      // cast the value to a date if the filter predicate is a date type.
      (isDateTimeColumnType(columnType) || columnType === "UNKNOWN") &&
      (isIsoDateArgs ||
        // Expected behavior for relative date filter is to filter by date:
        // see https://hex-tech-hq.slack.com/archives/C0789UJVB9S/p1728417987819679
        isRelativeDateArgs)
    ) {
      return {
        column: `ToDate(${column})`,
        columnType: "DATE",
        truncationApplied: false,
      };
    }
    return {
      column,
      columnType,
      truncationApplied: false,
    };
  },

  castDateOrDatetimeArg: castDateOrDatetimeArgImpl,

  processBinaryColumnPredicate: ({ arg, operation }) => ({ arg, operation }),
  normalizeBetweenBounds: ({
    args,
  }: {
    args: string[];
    columnType: FilledDynamicValueTableColumnType;
    truncationApplied: boolean;
  }) => {
    if (args.length !== 2) {
      throw new Error("Expected 2 arguments for a DATE_BETWEEN filter");
    }
    return {
      lowerArg: args[0]!,
      upperArg: args[1]!,
      lowerInclusive: true,
      upperInclusive: true,
    };
  },
  maybeRewriteDateEqual: () => undefined,
};

const optimizedDateFilterTransformations: DateFilterTransformations = {
  maybeDateCastColumn: ({
    args,
    column,
    columnType,
  }: {
    args: string[];
    column: string;
    columnType: FilledDynamicValueTableColumnType;
    operation?: string;
  }) => {
    // Keep WEEK behavior unchanged; otherwise avoid truncating the column for 0-offset
    const relativeDateArg =
      args.length === 1 ? tryParseRelativeDateOffsetValue(args[0]!) : null;
    if (
      relativeDateArg != null &&
      relativeDateArg.amount === 0 &&
      relativeDateArg.unit === RelativeDateUnit.WEEK
    ) {
      const truncated = applyRelativeDateTruncation(
        column,
        relativeDateArg.unit,
      );
      return { ...truncated, truncationApplied: true };
    }

    return {
      column,
      columnType,
      truncationApplied: false,
    };
  },

  castDateOrDatetimeArg: castDateOrDatetimeArgImpl,

  processBinaryColumnPredicate: ({
    arg,
    operation,
  }: {
    operation: string;
    arg: string;
  }) => {
    // Parse relative date, handle 0-offset first with early returns
    const parsedRelative = tryParseRelativeDateOffsetValue(arg);
    if (parsedRelative?.amount === 0) {
      const zeroOffsetRewrite = rewriteForZeroOffsetRelative(
        operation,
        parsedRelative.unit,
        parsedRelative.timezone,
      );
      if (zeroOffsetRewrite) return zeroOffsetRewrite;
      return { operation, arg };
    }

    // Resolve non-zero relative date to ISO, or pass through original
    const resolvedArg = parsedRelative
      ? computeRelativeDate(parsedRelative).format(DATE_FORMAT_NO_TIME)
      : arg;

    return rewriteIsoOpForResolvedDate(operation, resolvedArg);
  },

  normalizeBetweenBounds: ({
    args,
    columnType,
    truncationApplied,
  }: {
    args: string[];
    columnType: FilledDynamicValueTableColumnType;
    truncationApplied: boolean;
  }) => {
    if (args.length !== 2)
      throw new Error("Expected two arguments for DATE_BETWEEN filter");

    const startArg = args[0]!;
    const endArg = args[1]!;

    // Default: inclusive bounds
    const lowerArg = startArg;
    let upperArg = endArg;
    const lowerInclusive = true;
    let upperInclusive = true;

    // If truncation is applied on the column, do not optimize bounds
    if (truncationApplied) {
      return { lowerArg, upperArg, lowerInclusive, upperInclusive };
    }

    // Resolve relative end date if present
    const parsedRelative = tryParseRelativeDateOffsetValue(endArg);
    let resolvedEnd = endArg;
    if (parsedRelative != null && parsedRelative.amount !== 0) {
      resolvedEnd =
        computeRelativeDate(parsedRelative).format(DATE_FORMAT_NO_TIME);
    }

    // Optimize only for datetime-like columns with a valid ISO end date
    if (
      isDateTimeColumnType(columnType) &&
      resolvedEnd != null &&
      isIsoDate(resolvedEnd) &&
      moment(resolvedEnd).isValid()
    ) {
      const nextDay = moment(resolvedEnd).add(1, "day");
      upperArg = nextDay.format(DATE_FORMAT_NO_TIME);
      upperInclusive = false;
    }

    return { lowerArg, upperArg, lowerInclusive, upperInclusive };
  },
  maybeRewriteDateEqual: ({
    arg,
    columnType,
    hasJinja,
  }: {
    columnType: FilledDynamicValueTableColumnType;
    arg: string;
    hasJinja?: boolean;
  }) => {
    // Expand THIS day/month/quarter/year to [start, next-start) for datetime-like columns; keep WEEK unchanged
    const parsedRel = tryParseRelativeDateOffsetValue(arg);
    if (
      (columnType === "DATETIME" || columnType === "DATETIMETZ") &&
      !hasJinja &&
      parsedRel != null &&
      parsedRel.amount === 0 &&
      parsedRel.unit !== RelativeDateUnit.WEEK
    ) {
      const { lower, upper } = getZeroOffsetBoundsForUnit(
        parsedRel.unit,
        parsedRel.timezone,
      );
      return {
        lowerArg: lower,
        upperArg: upper,
      };
    }

    // Only expand for datetime-like columns, non-jinja, ISO date args
    if (
      (columnType === "DATETIME" || columnType === "DATETIMETZ") &&
      !hasJinja &&
      isIsoDate(arg)
    ) {
      const m = moment(arg);
      if (m.isValid()) {
        const nextDay = m.clone().add(1, "day");
        return {
          lowerArg: arg,
          upperArg: nextDay.format(DATE_FORMAT_NO_TIME),
        };
      }
    }
    return undefined;
  },
};

export function getDateFilterTransformations(mode?: DateFilterMode) {
  return mode === "optimized"
    ? optimizedDateFilterTransformations
    : legacyDateFilterTransformations;
}
